import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { createMerchantPost } from "../utils/merchantPostsFirestore";
import { searchUnsplashPhotos } from "../utils/unsplashAPI";
import { firebaseStorage } from "../firebase/firebaseClient";
import { useResolvedMerchantId } from "../hooks/useResolvedMerchantId";

const MAX_CHARS = 280;
const UNSPLASH_DEBOUNCE_MS = 800;
const UNSPLASH_MIN_TEXT_LEN = 5;

function PostComposer({ onPosted, merchantId: merchantIdProp, merchantName: merchantNameProp }) {
  const { userInfo } = useSelector((state) => state.userLogin);
  const { merchantId, businessName } = useResolvedMerchantId(userInfo, merchantIdProp, merchantNameProp);

  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const [photoUrl, setPhotoUrl] = useState(null);
  const [selectedPhotoId, setSelectedPhotoId] = useState(null);
  const [unsplashPhotos, setUnsplashPhotos] = useState([]);
  const [unsplashLoading, setUnsplashLoading] = useState(false);
  const [isUploadedPhoto, setIsUploadedPhoto] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const fileInputRef = useRef(null);

  const charsLeft = MAX_CHARS - text.length;
  const overLimit = charsLeft < 0;
  const isEmpty = text.trim().length === 0;

  // Debounced Unsplash search. Skipped when user has uploaded their own photo.
  useEffect(() => {
    const trimmed = text.trim();
    if (isUploadedPhoto || trimmed.length < UNSPLASH_MIN_TEXT_LEN) {
      setUnsplashPhotos([]);
      return;
    }

    const query = trimmed.split(/\s+/).slice(0, 5).join(" ");
    const timer = setTimeout(async () => {
      setUnsplashLoading(true);
      try {
        const results = await searchUnsplashPhotos(query);
        setUnsplashPhotos(results);
      } catch {
        // Silently skip — Unsplash is non-critical
      } finally {
        setUnsplashLoading(false);
      }
    }, UNSPLASH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [text, isUploadedPhoto]);

  const handleSelectPhoto = (photo) => {
    setPhotoUrl(photo.url);
    setSelectedPhotoId(photo.id);
  };

  const handleClearPhoto = () => {
    setPhotoUrl(null);
    setSelectedPhotoId(null);
    setIsUploadedPhoto(false);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadLoading(true);
    setError("");
    try {
      const path = `posts/${merchantId || "unknown"}/${Date.now()}_${file.name}`;
      const fileRef = storageRef(firebaseStorage, path);
      await uploadBytes(fileRef, file);
      const downloadUrl = await getDownloadURL(fileRef);
      setPhotoUrl(downloadUrl);
      setIsUploadedPhoto(true);
      setUnsplashPhotos([]);
      setSelectedPhotoId(null);
    } catch {
      setError("Could not upload photo. Please try again.");
    } finally {
      setUploadLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEmpty || overLimit || submitting) return;

    setSubmitting(true);
    setError("");
    setSuccess(false);

    if (!merchantId) {
      setError("Could not determine your business account. Please refresh the page and try again.");
      setSubmitting(false);
      return;
    }

    try {
      await createMerchantPost(merchantId, businessName, { text: text.trim(), photoUrl });
      setText("");
      setPhotoUrl(null);
      setSelectedPhotoId(null);
      setUnsplashPhotos([]);
      setIsUploadedPhoto(false);
      setSuccess(true);
      if (onPosted) onPosted();
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card">
      <div className="card-body">
        <h6 className="card-title mb-3">New post</h6>

        {success && (
          <div className="alert alert-success py-2 mb-3" role="status">
            Your post has been published.
          </div>
        )}

        {error && (
          <p className="text-danger small mb-2" role="alert">
            {error}
          </p>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-2">
            <textarea
              className="form-control"
              rows={3}
              maxLength={MAX_CHARS + 1}
              placeholder="Share an update with your customers…"
              value={text}
              onChange={(e) => {
                setSuccess(false);
                setText(e.target.value);
              }}
              disabled={submitting}
            />
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          {/* Unsplash photo suggestions */}
          {unsplashLoading && (
            <p className="text-muted small mb-2">Finding photos…</p>
          )}

          {!unsplashLoading && unsplashPhotos.length > 0 && (
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <p className="text-muted small mb-0">Suggested photos</p>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  style={{ fontSize: "0.75rem", padding: "2px 8px" }}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadLoading || submitting}
                >
                  {uploadLoading ? "Uploading…" : "Upload your own"}
                </button>
              </div>
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  overflowX: "auto",
                  paddingBottom: "4px",
                }}
              >
                {unsplashPhotos.map((photo) => (
                  <img
                    key={photo.id}
                    src={photo.thumb}
                    alt={photo.alt}
                    onClick={() => handleSelectPhoto(photo)}
                    style={{
                      width: "80px",
                      height: "60px",
                      objectFit: "cover",
                      borderRadius: "6px",
                      cursor: "pointer",
                      flexShrink: 0,
                      border: selectedPhotoId === photo.id
                        ? "2px solid #0d6efd"
                        : "2px solid transparent",
                      opacity: selectedPhotoId && selectedPhotoId !== photo.id ? 0.5 : 1,
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Upload button when Unsplash strip is not visible */}
          {unsplashPhotos.length === 0 && !unsplashLoading && !photoUrl && (
            <div className="mb-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                style={{ fontSize: "0.75rem" }}
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadLoading || submitting}
              >
                {uploadLoading ? "Uploading…" : "Upload photo"}
              </button>
            </div>
          )}

          {/* Selected photo preview */}
          {photoUrl && (
            <div className="mb-3 position-relative" style={{ display: "inline-block", width: "100%" }}>
              <img
                src={photoUrl}
                alt="Selected"
                className="rounded"
                style={{ width: "100%", maxHeight: "220px", objectFit: "cover" }}
              />
              <button
                type="button"
                className="btn btn-sm btn-dark"
                onClick={handleClearPhoto}
                style={{
                  position: "absolute",
                  top: "8px",
                  right: "8px",
                  padding: "2px 8px",
                  fontSize: "0.75rem",
                  opacity: 0.85,
                }}
              >
                × Remove
              </button>
            </div>
          )}

          <div className="d-flex justify-content-between align-items-center">
            <span
              className={`small ${overLimit ? "text-danger" : charsLeft <= 20 ? "text-warning" : "text-muted"}`}
            >
              {charsLeft}
            </span>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={isEmpty || overLimit || submitting}
            >
              {submitting ? "Publishing…" : "Publish"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default PostComposer;
